import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { FileText, Download, Share2, Search, Plus, Minus, ShoppingCart, Trash2, Package, RotateCcw, AlertTriangle, BadgeCheck, Camera, Upload, Eye, X } from "lucide-react";
import { enviarWhatsApp, buildVarsFromPromissoria, resolverMensagem } from "../lib/whatsappTemplates";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function NovaPromissoriaPage() {
  const navigate = useNavigate();
  // Editar modo: ?editar=<promissoria_id>
  const searchParams = new URLSearchParams(window.location.search);
  const editandoId = searchParams.get("editar");
  const modoEdicao = Boolean(editandoId);

  const [clientes, setClientes] = useState([]);
  const [buscarCliente, setBuscarCliente] = useState("");
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showClienteList, setShowClienteList] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [createdPromissoria, setCreatedPromissoria] = useState(null);
  const [promissoriaEditando, setPromissoriaEditando] = useState(null);
  const [loadingEdicao, setLoadingEdicao] = useState(modoEdicao);
  // Modo edição: foto da nova venda + impressão obrigatórias
  const [fotoVendaEdicaoPath, setFotoVendaEdicaoPath] = useState("");
  const [uploadingFotoEdicao, setUploadingFotoEdicao] = useState(false);
  const [termicoImpresso, setTermicoImpresso] = useState(false);
  // Foto da nota (comprovante da venda) — anexada direto no preview após criar
  const [uploadingFotoNota, setUploadingFotoNota] = useState(false);
  const [fotosAnexadas, setFotosAnexadas] = useState([]);

  const [estoqueCarro, setEstoqueCarro] = useState([]);
  const [carrinho, setCarrinho] = useState([]);
  const [buscaProduto, setBuscaProduto] = useState("");
  const [modoRetorno, setModoRetorno] = useState(false);
  const [entradas, setEntradas] = useState([]);
  const [showEntradaForm, setShowEntradaForm] = useState(false);
  const [entradaForm, setEntradaForm] = useState({ valor: '', forma_pagamento: 'PIX', imagem_path: '' });
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const [previewComprovantePath, setPreviewComprovantePath] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Venda à Vista
  const [vendaAvista, setVendaAvista] = useState(false);

  // Nota pendente
  const [notaPendente, setNotaPendente] = useState(null);
  const [checkingPendente, setCheckingPendente] = useState(false);

  const [formData, setFormData] = useState({
    cliente_id: "",
    numero_pedido: "",
    valor_total: "",
    data_compra: "",
    data_vencimento: ""
  });
  const [loadingNumero, setLoadingNumero] = useState(true);

  useEffect(() => {
    loadClientes();
    if (!modoEdicao) {
      loadNextNumber();
    } else {
      // Carrega a promissória a editar
      (async () => {
        try {
          const { data: prom } = await axios.get(`${API}/promissorias/${editandoId}`);
          setPromissoriaEditando(prom);
          // Pré-preenche o form
          setFormData({
            cliente_id: prom.cliente_id,
            numero_pedido: String(prom.numero_pedido || ""),
            valor_total: String(prom.valor_total || ""),
            data_compra: prom.data_compra || "",
            data_vencimento: prom.data_vencimento || "",
          });
          setVendaAvista(Boolean(prom.venda_avista));
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Erro ao carregar nota para edição");
          navigate(-1);
        } finally {
          setLoadingEdicao(false);
        }
      })();
    }
  }, []);// eslint-disable-line

  useEffect(() => {
    if (modoEdicao && promissoriaEditando && clientes.length > 0 && !selectedCliente) {
      const cli = clientes.find(c => c.id === promissoriaEditando.cliente_id);
      if (cli) {
        setSelectedCliente(cli);
        setBuscarCliente(cli.nome_completo);
        setShowClienteList(false);
        // Carrega estoque do carro do cliente
        if (cli.carro_id) loadEstoqueCarro(cli.carro_id);
        // Pré-carrega o carrinho com os itens da nota
        const itensCart = (promissoriaEditando.itens || []).map(it => ({
          produto_id: it.produto_id,
          produto_nome: it.produto_nome,
          produto_codigo: it.produto_codigo || "",
          quantidade: it.quantidade,
          preco_unitario: it.preco_unitario,
          subtotal: it.subtotal || (it.preco_unitario * it.quantidade),
          tipo: it.tipo || "venda",
        }));
        setCarrinho(itensCart);
      }
    }
  }, [clientes, promissoriaEditando]);// eslint-disable-line

  const loadNextNumber = async () => {
    try {
      const response = await axios.get(`${API}/promissorias/next-number`);
      setFormData(prev => ({ ...prev, numero_pedido: String(response.data.next_number) }));
    } catch (error) {
      console.error("Erro ao carregar numero do pedido:", error);
    } finally {
      setLoadingNumero(false);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await axios.get(`${API}/clientes`);
      setClientes(response.data);
    } catch (error) {
      toast.error("Erro ao carregar clientes");
    }
  };

  const loadEstoqueCarro = async (carroId) => {
    try {
      const response = await axios.get(`${API}/estoques/carro/${carroId}`);
      setEstoqueCarro(response.data);
    } catch (error) {
      setEstoqueCarro([]);
    }
  };

  const checkNotaPendente = async (clienteId) => {
    setCheckingPendente(true);
    try {
      const response = await axios.get(`${API}/clientes/${clienteId}/nota-pendente`);
      setNotaPendente(response.data.tem_pendente ? response.data.nota : null);
    } catch {
      setNotaPendente(null);
    } finally {
      setCheckingPendente(false);
    }
  };

  const clientesFiltrados = clientes.filter(cliente => {
    if (!buscarCliente.trim()) return true;
    const termo = buscarCliente.toLowerCase();
    return (
      cliente.nome_completo.toLowerCase().includes(termo) ||
      cliente.cpf_rg.toLowerCase().includes(termo) ||
      (cliente.telefone && cliente.telefone.toLowerCase().includes(termo))
    );
  });

  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    setFormData(prev => ({...prev, cliente_id: cliente.id}));
    setBuscarCliente(cliente.nome_completo);
    setShowClienteList(false);
    setNotaPendente(null);
    if (cliente.bloqueado) {
      toast.error("Cliente bloqueada! Nao e possivel criar promissorias para clientes bloqueados.");
    }
    checkNotaPendente(cliente.id);
    if (cliente.carro_id) loadEstoqueCarro(cliente.carro_id);
    setCarrinho([]);
    setModoRetorno(false);
    setEntradas([]);
    setVendaAvista(false);
  };

  const handleClearCliente = () => {
    setSelectedCliente(null);
    setFormData(prev => ({...prev, cliente_id: "", valor_total: ""}));
    setBuscarCliente("");
    setEstoqueCarro([]);
    setCarrinho([]);
    setModoRetorno(false);
    setNotaPendente(null);
    setEntradas([]);
    setVendaAvista(false);
  };

  const addToCart = (produto, tipo = "venda") => {
    const existente = carrinho.find(i => i.produto_id === produto.produto_id && i.tipo === tipo);
    if (existente) {
      if (tipo === "venda") {
        const totalVendaNoCarrinho = carrinho
          .filter(i => i.produto_id === produto.produto_id && i.tipo === "venda")
          .reduce((sum, i) => sum + i.quantidade, 0);
        if (totalVendaNoCarrinho >= produto.quantidade) {
          toast.error(`Estoque maximo: ${produto.quantidade} unidades`);
          return;
        }
      }
      const updated = carrinho.map(i =>
        (i.produto_id === produto.produto_id && i.tipo === tipo)
          ? { ...i, quantidade: i.quantidade + 1, subtotal: (i.quantidade + 1) * i.preco_unitario }
          : i
      );
      setCarrinho(updated);
      updateTotal(updated);
    } else {
      const preco = precoDoProduto(produto);
      const novoItem = {
        produto_id: produto.produto_id,
        produto_nome: produto.produto_nome,
        produto_codigo: produto.produto_codigo,
        quantidade: 1,
        preco_unitario: preco,
        subtotal: preco,
        max_estoque: produto.quantidade,
        tipo
      };
      const updated = [...carrinho, novoItem];
      setCarrinho(updated);
      updateTotal(updated);
    }
  };

  const removeFromCart = (produtoId, tipo) => {
    const updated = carrinho.filter(i => !(i.produto_id === produtoId && i.tipo === tipo));
    setCarrinho(updated);
    updateTotal(updated);
  };

  const updateQuantity = (produtoId, tipo, novaQtd) => {
    const item = carrinho.find(i => i.produto_id === produtoId && i.tipo === tipo);
    if (!item) return;
    if (novaQtd < 1) { removeFromCart(produtoId, tipo); return; }
    if (tipo === "venda" && novaQtd > item.max_estoque) {
      toast.error(`Estoque maximo: ${item.max_estoque} unidades`);
      return;
    }
    const updated = carrinho.map(i =>
      (i.produto_id === produtoId && i.tipo === tipo)
        ? { ...i, quantidade: novaQtd, subtotal: novaQtd * i.preco_unitario }
        : i
    );
    setCarrinho(updated);
    updateTotal(updated);
  };

  const updatePrice = (produtoId, tipo, novoPreco) => {
    const preco = parseFloat(novoPreco) || 0;
    const updated = carrinho.map(i =>
      (i.produto_id === produtoId && i.tipo === tipo)
        ? { ...i, preco_unitario: preco, subtotal: i.quantidade * preco }
        : i
    );
    setCarrinho(updated);
    updateTotal(updated);
  };

  const updateTotal = (items) => {
    const totalVendas = items.filter(i => i.tipo === "venda").reduce((acc, i) => acc + i.subtotal, 0);
    const totalRetornos = items.filter(i => i.tipo === "retorno").reduce((acc, i) => acc + i.subtotal, 0);
    const total = totalVendas - totalRetornos;
    setFormData(prev => ({ ...prev, valor_total: total.toFixed(2) }));
  };

  const produtosFiltrados = estoqueCarro.filter(est => {
    if (modoRetorno) {
      // No retorno, mostrar todos os produtos do carro
    } else {
      if (est.quantidade <= 0) return false;
    }
    if (!buscaProduto.trim()) return true;
    const termo = buscaProduto.toLowerCase();
    return (
      est.produto_nome.toLowerCase().includes(termo) ||
      est.produto_codigo.toLowerCase().includes(termo)
    );
  });

  const itensVenda = carrinho.filter(i => i.tipo === "venda");
  const itensRetorno = carrinho.filter(i => i.tipo === "retorno");
  const totalVendas = itensVenda.reduce((acc, i) => acc + i.subtotal, 0);
  const totalRetornos = itensRetorno.reduce((acc, i) => acc + i.subtotal, 0);
  const totalBruto = totalVendas - totalRetornos;

  // Desconto 5% para venda à vista
  const descontoAvista = vendaAvista ? totalBruto * 0.05 : 0;
  const totalFinal = totalBruto - descontoAvista;

  // Limite de compra (só aplica para venda a prazo, não à vista)
  const tipoCliente = selectedCliente?.tipo_cliente || 'salao_revenda';
  const precoDoProduto = (produto) => {
    if (!produto) return 0;
    if (tipoCliente === 'cliente_final' && produto.preco_cliente_final != null && produto.preco_cliente_final > 0) {
      return Number(produto.preco_cliente_final);
    }
    return Number(produto.preco_venda || 0);
  };

  const limiteCompra = selectedCliente?.limite_compra;
  const temLimite = !vendaAvista && limiteCompra != null && limiteCompra > 0;
  const excedente = temLimite ? Math.max(0, totalFinal - limiteCompra) : 0;
  const entradaValor = entradas.reduce((acc, e) => acc + e.valor, 0);
  const entradaInsuficiente = vendaAvista ? false : (excedente > 0 && entradaValor < excedente);

  // Venda à vista: só libera se total quitado
  const avistaQuitado = vendaAvista && entradaValor >= totalFinal - 0.01;

  // Auto-set data vencimento para à vista
  useEffect(() => {
    if (vendaAvista && formData.data_compra) {
      const dt = new Date(formData.data_compra + 'T12:00:00');
      dt.setDate(dt.getDate() + 30);
      const venc = dt.toISOString().split('T')[0];
      setFormData(prev => ({ ...prev, data_vencimento: venc }));
    }
  }, [vendaAvista, formData.data_compra]);

  const handleToggleAvista = () => {
    const next = !vendaAvista;
    setVendaAvista(next);
    if (next) {
      // Resetar entradas ao mudar para à vista
      setEntradas([]);
      setShowEntradaForm(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (itensVenda.length === 0) {
      toast.error("Adicione pelo menos um produto de venda");
      return;
    }
    if (vendaAvista && !avistaQuitado) {
      toast.error("Para venda à vista, o valor total deve ser quitado.");
      return;
    }
    if (!vendaAvista && entradaInsuficiente) {
      toast.error(`Limite excedido em R$ ${excedente.toFixed(2)}. Entrada mínima obrigatória: R$ ${excedente.toFixed(2)}`);
      return;
    }

    try {
      setSubmitting(true);
      const itensPayload = carrinho.map(({ produto_id, produto_nome, produto_codigo, quantidade, preco_unitario, subtotal, tipo }) => ({
        produto_id, produto_nome, produto_codigo, quantidade, preco_unitario, subtotal, tipo
      }));

      if (modoEdicao) {
        if (!fotoVendaEdicaoPath) {
          toast.error("Tire/envie a nova foto da venda antes de salvar.");
          setSubmitting(false);
          return;
        }
        // No fluxo de edição, só permitimos UMA "entrada_adicional" no momento
        const entrada_adicional = entradas.length > 0 ? entradas[entradas.length - 1] : null;
        const response = await axios.put(`${API}/promissorias/${editandoId}/editar`, {
          numero_pedido: formData.numero_pedido,
          valor_total: parseFloat(totalFinal.toFixed(2)),
          descricao_compra: "",
          data_vencimento: formData.data_vencimento,
          itens: itensPayload,
          entrada_adicional,
        });
        // Recarrega o objeto completo da promissoria atualizada
        try {
          const { data: prom } = await axios.get(`${API}/promissorias/${editandoId}`);
          setCreatedPromissoria(prom);
        } catch {
          setCreatedPromissoria({ ...promissoriaEditando, ...response.data });
        }
        setShowPreview(true);
        toast.success("Nota atualizada! Tire a nova foto da venda e imprima o PDF térmico.");
        try { window.dispatchEvent(new CustomEvent("venda-criada")); } catch {}
        if (selectedCliente?.carro_id) loadEstoqueCarro(selectedCliente.carro_id);
        return;
      }

      const response = await axios.post(`${API}/promissorias`, {
        ...formData,
        descricao_compra: "",
        valor_total: parseFloat(totalFinal.toFixed(2)),
        assinatura_base64: "",
        itens: itensPayload,
        entrada: entradas.length > 0 ? entradas : null,
        venda_avista: vendaAvista
      });

      setCreatedPromissoria(response.data);
      setShowPreview(true);
      toast.success(vendaAvista ? "Venda à vista concluída!" : "Promissoria criada! Estoque atualizado.");
      // Avisa o painel "Meta Diária / Semanal" para recarregar
      try { window.dispatchEvent(new CustomEvent("venda-criada")); } catch {}

      if (selectedCliente?.carro_id) loadEstoqueCarro(selectedCliente.carro_id);
      loadNextNumber();
    } catch (error) {
      toast.error(error.response?.data?.detail || (modoEdicao ? "Erro ao atualizar promissória" : "Erro ao criar promissoria"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadFotoNota = async (file) => {
    if (!file || !createdPromissoria?.id) return;
    setUploadingFotoNota(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/promissorias/${createdPromissoria.id}/foto`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFotosAnexadas(prev => [...prev, data]);
      toast.success("Foto anexada!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar foto");
    } finally {
      setUploadingFotoNota(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/promissorias/${createdPromissoria.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Recibo_${createdPromissoria.numero_pedido}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Recibo baixado!");
      setTermicoImpresso(true);
    } catch (error) {
      toast.error("Erro ao baixar recibo");
    }
  };

  const uploadFotoVendaEdicao = async (file) => {
    if (!file) return;
    setUploadingFotoEdicao(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/promissorias/${editandoId}/foto`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFotoVendaEdicaoPath(data.id || data.path || "ok");
      toast.success("Foto da venda enviada!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar foto da venda");
    } finally {
      setUploadingFotoEdicao(false);
    }
  };

  const uploadComprovante = async (file) => {
    if (!file) return null;
    setUploadingComprovante(true);
    try {
      const fd = new FormData();
      fd.append("imagem", file);
      const { data } = await axios.post(`${API}/comprovantes/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Comprovante enviado!");
      return data.imagem_path;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar comprovante");
      return null;
    } finally {
      setUploadingComprovante(false);
    }
  };

  const handleComprovanteFile = async (file) => {
    if (!file) return;
    const path = await uploadComprovante(file);
    if (path) setEntradaForm((prev) => ({ ...prev, imagem_path: path }));
  };

  const handleShareWhatsApp = async () => {
    if (!createdPromissoria || !selectedCliente) return;
    const vars = buildVarsFromPromissoria({
      promissoria: createdPromissoria,
      cliente: selectedCliente,
    });
    await enviarWhatsApp({
      gatilho: "nova_promissoria",
      telefone: selectedCliente.telefone,
      vars,
    });
  };

  const formatDate = (ds) => {
    if (!ds) return '';
    const [y, m, d] = ds.split('-');
    return `${d}/${m}/${y}`;
  };

  const renderProductList = (tipo) => (
    <div className="border rounded-lg max-h-48 overflow-y-auto">
      {produtosFiltrados.length === 0 ? (
        <div className="p-4 text-center text-gray-500 text-sm">
          <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          {estoqueCarro.length === 0 ? "Nenhum produto neste carro" : "Nenhum produto encontrado"}
        </div>
      ) : (
        produtosFiltrados.map((produto) => {
          const noCarrinho = carrinho.find(i => i.produto_id === produto.produto_id && i.tipo === tipo);
          const qtdDisponivel = tipo === "venda"
            ? produto.quantidade - (noCarrinho?.quantidade || 0)
            : 999;
          return (
            <div key={produto.produto_id} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 hover:bg-gray-50">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{produto.produto_nome}</p>
                <p className="text-xs text-gray-500">SKU: {produto.produto_codigo} {tipo === "venda" ? `| Disp: ${produto.quantidade - (noCarrinho?.quantidade || 0)}` : ""}</p>
              </div>
              <div className="flex items-center gap-2 ml-2">
                <span className={`text-sm font-semibold whitespace-nowrap ${tipo === "retorno" ? "text-orange-600" : "text-green-600"}`}>
                  R$ {precoDoProduto(produto).toFixed(2)}
                </span>
                <Button type="button" size="sm" variant="outline" onClick={() => addToCart(produto, tipo)}
                  disabled={tipo === "venda" && qtdDisponivel <= 0} className="h-7 w-7 p-0"
                  data-testid={`add-${tipo}-${produto.produto_id}`}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const renderCartItems = (items, tipo) => {
    if (items.length === 0) return null;
    const isRetorno = tipo === "retorno";
    return (
      <div className={`${isRetorno ? "bg-orange-50 border-orange-200" : "bg-green-50 border-green-200"} border rounded-lg p-4 space-y-3`}
        data-testid={`cart-${tipo}`}>
        <p className={`font-semibold flex items-center gap-2 ${isRetorno ? "text-orange-800" : "text-green-800"}`}>
          {isRetorno ? <RotateCcw className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
          {isRetorno ? "Retorno" : "Venda"} ({items.length} {items.length === 1 ? 'item' : 'itens'})
        </p>
        {items.map((item) => (
          <div key={`${item.produto_id}-${item.tipo}`} className="flex items-center justify-between bg-white rounded p-2 border">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{item.produto_nome}</p>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs text-gray-500">R$</span>
                <input type="number" step="0.01" min="0" value={item.preco_unitario}
                  onChange={(e) => updatePrice(item.produto_id, item.tipo, e.target.value)}
                  className="w-16 text-xs border rounded px-1 py-0.5 text-center font-semibold" />
                <span className="text-xs text-gray-500">un.</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0"
                onClick={() => updateQuantity(item.produto_id, item.tipo, item.quantidade - 1)}>
                <Minus className="h-3 w-3" />
              </Button>
              <span className="text-sm font-bold w-8 text-center">{item.quantidade}</span>
              <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0"
                onClick={() => updateQuantity(item.produto_id, item.tipo, item.quantidade + 1)}>
                <Plus className="h-3 w-3" />
              </Button>
              <span className={`text-sm font-semibold w-20 text-right ${isRetorno ? "text-orange-700" : "text-green-700"}`}>
                {isRetorno ? "- " : ""}R$ {item.subtotal.toFixed(2)}
              </span>
              <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
                onClick={() => removeFromCart(item.produto_id, item.tipo)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
        <div className={`flex justify-between items-center pt-2 border-t ${isRetorno ? "border-orange-300" : "border-green-300"}`}>
          <span className={`font-bold ${isRetorno ? "text-orange-800" : "text-green-800"}`}>
            {isRetorno ? "DESCONTO RETORNO:" : "SUBTOTAL VENDA:"}
          </span>
          <span className={`text-lg font-bold ${isRetorno ? "text-orange-700" : "text-green-700"}`}>
            {isRetorno ? "- " : ""}R$ {items.reduce((a, i) => a + i.subtotal, 0).toFixed(2)}
          </span>
        </div>
      </div>
    );
  };

  // Blocked states
  const clienteBloqueado = selectedCliente?.bloqueado;
  const temNotaPendente = !!notaPendente;
  const bloqueadoVenda = clienteBloqueado || temNotaPendente;

  const submitDisabled = submitting
    || itensVenda.length === 0
    || !formData.cliente_id
    || bloqueadoVenda
    || (vendaAvista ? !avistaQuitado : entradaInsuficiente)
    || (modoEdicao && !fotoVendaEdicaoPath);

  const getSubmitLabel = () => {
    if (submitting) return modoEdicao ? 'Atualizando...' : (vendaAvista ? 'Concluindo Venda...' : 'Gerando Promissória...');
    if (bloqueadoVenda) return temNotaPendente ? 'Cliente com Nota Pendente' : 'Cliente Bloqueado';
    if (vendaAvista && !avistaQuitado) return `Faltam R$ ${(totalFinal - entradaValor).toFixed(2)} para quitar`;
    if (!vendaAvista && entradaInsuficiente) return `Entrada mínima: R$ ${excedente.toFixed(2)}`;
    if (modoEdicao && !fotoVendaEdicaoPath) return 'Tire a foto da venda para continuar';
    if (modoEdicao) return `Atualizar Nota - R$ ${totalFinal.toFixed(2)}`;
    if (vendaAvista) return `CONCLUIR VENDA - R$ ${totalFinal.toFixed(2)}`;
    return `Salvar Promissoria - R$ ${totalFinal.toFixed(2)}`;
  };

  return (
    <>
      <div className="max-w-4xl mx-auto" data-testid="nova-promissoria-page">
        {modoEdicao && (
          <div className="mb-3 bg-amber-50 border-2 border-amber-300 rounded-lg p-3 space-y-3" data-testid="banner-modo-edicao">
            <div className="flex items-start gap-2 text-sm">
              <RotateCcw className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-bold text-amber-900">Editando nota existente</p>
                <p className="text-amber-800 text-xs">Os pagamentos anteriores serão preservados. O estoque do carro é ajustado automaticamente. Ao salvar, é <strong>obrigatório</strong> tirar nova foto da venda e imprimir o PDF térmico.</p>
              </div>
            </div>

            {/* Foto da Venda obrigatória ANTES de salvar */}
            <div className="bg-white border border-amber-300 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-amber-900 uppercase">Foto da nova venda (obrigatória) <span className="text-red-600">*</span></p>
                {fotoVendaEdicaoPath && <BadgeCheck className="h-5 w-5 text-emerald-600" />}
              </div>
              {fotoVendaEdicaoPath ? (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
                  <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="text-xs text-emerald-800 flex-1">Foto enviada — pronto para salvar.</span>
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => setFotoVendaEdicaoPath("")} title="Trocar foto" data-testid="btn-remover-foto-edicao">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <input type="file" accept="image/*" capture="environment" id="foto-edicao-camera" hidden onChange={(e) => uploadFotoVendaEdicao(e.target.files?.[0])} data-testid="input-foto-edicao-camera" />
                  <input type="file" accept="image/*" id="foto-edicao-upload" hidden onChange={(e) => uploadFotoVendaEdicao(e.target.files?.[0])} data-testid="input-foto-edicao-upload" />
                  <Button type="button" variant="outline" disabled={uploadingFotoEdicao} onClick={() => document.getElementById('foto-edicao-camera')?.click()} className="gap-1.5 text-indigo-700 border-indigo-300 hover:bg-indigo-50" data-testid="btn-foto-edicao-camera">
                    <Camera className="h-4 w-4" /> {uploadingFotoEdicao ? "Enviando..." : "Foto Venda"}
                  </Button>
                  <Button type="button" variant="outline" disabled={uploadingFotoEdicao} onClick={() => document.getElementById('foto-edicao-upload')?.click()} className="gap-1.5 text-purple-700 border-purple-300 hover:bg-purple-50" data-testid="btn-foto-edicao-upload">
                    <Upload className="h-4 w-4" /> Upload Foto
                  </Button>
                </div>
              )}
              <p className="text-[10px] text-amber-700 mt-1">A foto antiga permanece no histórico — esta será a 2ª foto da venda.</p>
            </div>
          </div>
        )}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl" style={{fontFamily: 'Work Sans'}}>{modoEdicao ? "Editar Promissória" : "Nova Promissoria Digital"}</CardTitle>
              {/* Toggle Venda à Vista */}
              <Button
                type="button"
                variant={vendaAvista ? "default" : "outline"}
                onClick={handleToggleAvista}
                className={vendaAvista
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  : "border-emerald-500 text-emerald-700 hover:bg-emerald-50 gap-2"}
                data-testid="btn-venda-avista"
              >
                <BadgeCheck className="h-4 w-4" />
                VENDA À VISTA
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Buscar Cliente */}
              <div className="space-y-2">
                <Label>Buscar e Selecionar Cliente *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                  <Input type="text" placeholder="Nome, CPF ou telefone..." value={buscarCliente}
                    onChange={(e) => { if (modoEdicao) return; setBuscarCliente(e.target.value); setShowClienteList(true); if (!e.target.value) handleClearCliente(); }}
                    onFocus={() => { if (!modoEdicao) setShowClienteList(true); }} className="pl-10" data-testid="search-cliente-input" autoComplete="off"
                    readOnly={modoEdicao} disabled={modoEdicao} />
                  {showClienteList && buscarCliente && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {clientesFiltrados.length > 0 ? clientesFiltrados.map((c) => (
                        <div key={c.id} onClick={() => handleSelectCliente(c)}
                          className={`px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 ${c.bloqueado ? 'bg-red-50' : ''}`}>
                          <div className={`font-medium text-sm ${c.bloqueado ? 'text-red-600' : ''}`}>
                            {c.nome_completo} {c.bloqueado && <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded ml-2">BLOQUEADA</span>}
                          </div>
                          <div className="text-xs text-gray-500">CPF: {c.cpf_rg} | Tel: {c.telefone || 'N/A'}</div>
                        </div>
                      )) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">Nenhum cliente encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                {selectedCliente && (
                  <div className={`p-3 rounded-md flex justify-between items-center ${clienteBloqueado ? 'bg-red-50 border-2 border-red-400' : temNotaPendente ? 'bg-amber-50 border-2 border-amber-400' : 'bg-blue-50 border border-blue-200'}`}>
                    <div>
                      <div className={`font-medium text-sm ${clienteBloqueado ? 'text-red-700' : ''}`}>
                        Cliente: {selectedCliente.nome_completo}
                        {tipoCliente === 'cliente_final' ? (
                          <span className="text-[10px] bg-pink-600 text-white px-1.5 py-0.5 rounded ml-2 font-bold" data-testid="badge-tipo-final">CLIENTE FINAL</span>
                        ) : (
                          <span className="text-[10px] bg-purple-600 text-white px-1.5 py-0.5 rounded ml-2 font-bold" data-testid="badge-tipo-salao">SALÃO/REVENDA</span>
                        )}
                        {clienteBloqueado && <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded ml-2">BLOQUEADA</span>}
                      </div>
                      <div className="text-xs text-gray-600">CPF: {selectedCliente.cpf_rg} | Tel: {selectedCliente.telefone}</div>
                      {selectedCliente.limite_compra != null && selectedCliente.limite_compra > 0 && (
                        <div className="text-xs font-semibold text-blue-700 mt-0.5" data-testid="cliente-limite-info">
                          Limite de Compra: R$ {selectedCliente.limite_compra.toFixed(2)}
                        </div>
                      )}
                      {clienteBloqueado && (
                        <div className="text-xs text-red-600 font-bold mt-1">Cliente bloqueada! Nao e possivel criar promissorias.</div>
                      )}
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={handleClearCliente} className="text-red-500">Limpar</Button>
                  </div>
                )}
                {/* Alerta de nota pendente */}
                {temNotaPendente && (
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3 flex items-start gap-3" data-testid="alerta-nota-pendente">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-800 font-bold text-sm">Cliente com Nota em ABERTO. Só é possível uma NOVA VENDA mediante o pagamento total da nota.</p>
                      <p className="text-amber-700 text-xs mt-0.5">
                        Pedido #{notaPendente.numero_pedido} | Total: R$ {notaPendente.valor_total?.toFixed(2)} | Pago: R$ {notaPendente.valor_pago?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
                <input type="hidden" value={formData.cliente_id} required />
              </div>

              {/* Numero e Datas */}
              <div className={`grid grid-cols-1 gap-4 ${vendaAvista ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                <div className="space-y-2">
                  <Label>N. Pedido *</Label>
                  <Input data-testid="numero-pedido-input" type="text" required readOnly
                    value={loadingNumero ? "..." : formData.numero_pedido} className="bg-gray-100 cursor-not-allowed font-bold" />
                </div>
                <div className="space-y-2">
                  <Label>Data da Compra *</Label>
                  <Input data-testid="data-compra-input" type="date" required value={formData.data_compra}
                    onChange={(e) => setFormData({...formData, data_compra: e.target.value})} />
                </div>
                {!vendaAvista && (
                  <div className="space-y-2">
                    <Label>Data de Vencimento *</Label>
                    <Input data-testid="vencimento-input" type="date" required value={formData.data_vencimento}
                      onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})} />
                  </div>
                )}
              </div>

              {/* Tarja verde Venda à Vista */}
              {vendaAvista && (
                <div className="bg-emerald-50 border-2 border-emerald-400 rounded-lg p-4" data-testid="tarja-avista">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="h-5 w-5 text-emerald-600" />
                      <span className="font-bold text-emerald-800 text-base">VENDA À VISTA</span>
                    </div>
                    <span className="text-emerald-700 font-bold text-lg">Desconto 5%</span>
                  </div>
                  {totalBruto > 0 && (
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="text-emerald-700">Subtotal: R$ {totalBruto.toFixed(2)}</span>
                      <span className="text-emerald-600 font-semibold">- R$ {descontoAvista.toFixed(2)}</span>
                      <span className="text-emerald-800 font-bold text-base">= R$ {totalFinal.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Carrinho */}
              {selectedCliente && !bloqueadoVenda && (
                <div className="space-y-4" data-testid="cart-section">
                  {/* Toggle Venda/Retorno */}
                  <div className="flex items-center gap-3 border-b pb-2">
                    <Button type="button" variant={!modoRetorno ? "default" : "outline"} size="sm"
                      onClick={() => setModoRetorno(false)} className={!modoRetorno ? "bg-green-600 hover:bg-green-700" : ""}
                      data-testid="btn-modo-venda">
                      <ShoppingCart className="h-4 w-4 mr-1" /> Venda
                    </Button>
                    <Button type="button" variant={modoRetorno ? "default" : "outline"} size="sm"
                      onClick={() => setModoRetorno(true)} className={modoRetorno ? "bg-orange-600 hover:bg-orange-700" : ""}
                      data-testid="btn-modo-retorno">
                      <RotateCcw className="h-4 w-4 mr-1" /> Retorno
                    </Button>
                    {modoRetorno && (
                      <span className="text-xs text-orange-600 font-medium">Modo devolucao ativo - produtos serao devolvidos ao estoque</span>
                    )}
                  </div>

                  {/* Busca de Produto */}
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input type="text" placeholder="Buscar produto por nome ou SKU..."
                      value={buscaProduto} onChange={(e) => setBuscaProduto(e.target.value)}
                      className="pl-10" data-testid="search-produto-input" />
                  </div>

                  {/* Lista de Produtos */}
                  {renderProductList(modoRetorno ? "retorno" : "venda")}

                  {/* Itens no Carrinho */}
                  {renderCartItems(itensVenda, "venda")}
                  {renderCartItems(itensRetorno, "retorno")}

                  {/* Total Final */}
                  {carrinho.length > 0 && (
                    <div className="space-y-3">
                      <div className={`rounded-lg p-4 flex justify-between items-center ${vendaAvista ? 'bg-emerald-900 text-white' : 'bg-gray-900 text-white'}`} data-testid="cart-total-final">
                        <span className="font-bold text-lg">TOTAL FINAL:</span>
                        <div className="text-right">
                          {vendaAvista && descontoAvista > 0 && (
                            <span className="text-sm line-through text-gray-400 mr-2">R$ {totalBruto.toFixed(2)}</span>
                          )}
                          <span className="text-2xl font-bold">R$ {totalFinal.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Alerta de excedente de limite */}
                      {!vendaAvista && temLimite && excedente > 0 && (
                        <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3" data-testid="alerta-excedente-limite">
                          <p className="text-red-700 font-bold text-sm">Limite de compra excedido!</p>
                          <p className="text-red-600 text-xs">Limite: R$ {limiteCompra.toFixed(2)} | Excedente: R$ {excedente.toFixed(2)}</p>
                          <p className="text-red-700 text-xs font-semibold mt-1">Entrada obrigatória de no mínimo R$ {excedente.toFixed(2)}</p>
                        </div>
                      )}

                      {/* Forma de Pagamento / Entradas */}
                      <Button type="button" onClick={() => setShowEntradaForm(!showEntradaForm)}
                        className="w-full gap-2 bg-blue-600 hover:bg-blue-700" data-testid="btn-entrada">
                        <Plus className="h-4 w-4" /> {vendaAvista ? 'FORMA DE PAGAMENTO' : 'ENTRADA R$'}
                      </Button>

                      {entradas.map((ent, idx) => (
                        <div key={idx} className="bg-blue-50 border border-blue-300 rounded-lg p-3 flex justify-between items-center">
                          <div>
                            <span className="text-xs text-blue-600 font-semibold">
                              {vendaAvista ? `PAGAMENTO ${idx + 1}` : `ENTRADA ${idx + 1}`} ({ent.forma_pagamento})
                            </span>
                            <p className="text-lg font-bold text-blue-700">R$ {ent.valor.toFixed(2)}</p>
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setEntradas(prev => prev.filter((_, i) => i !== idx))}
                            className="text-red-500 hover:text-red-700" data-testid={`btn-remover-entrada-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}

                      {showEntradaForm && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold">Forma de Pagamento</Label>
                            <select value={entradaForm.forma_pagamento}
                              onChange={(e) => setEntradaForm({...entradaForm, forma_pagamento: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              data-testid="entrada-forma-select">
                              <option value="PIX">PIX</option>
                              <option value="CR">Credito</option>
                              <option value="DB">Debito</option>
                              <option value="DH">Dinheiro</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold">{vendaAvista ? 'Valor R$' : 'Valor da Entrada R$'}</Label>
                            <Input type="number" step="0.01" min="0.01" max={totalFinal}
                              value={entradaForm.valor}
                              onChange={(e) => setEntradaForm({...entradaForm, valor: e.target.value})}
                              placeholder="0.00" data-testid="entrada-valor-input" />
                          </div>

                          {/* Comprovante obrigatório */}
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold flex items-center gap-1.5">
                              Comprovante <span className="text-red-600">*</span>
                              {entradaForm.imagem_path && <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />}
                            </Label>
                            {entradaForm.imagem_path ? (
                              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
                                <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                                <span className="text-xs text-emerald-800 flex-1 truncate">Comprovante enviado</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2"
                                  onClick={() => setPreviewComprovantePath(entradaForm.imagem_path)}
                                  data-testid="btn-preview-comprovante-entrada"
                                  title="Visualizar"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-red-600"
                                  onClick={() => setEntradaForm({ ...entradaForm, imagem_path: '' })}
                                  data-testid="btn-remover-comprovante-entrada"
                                  title="Trocar"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="file"
                                  accept="image/*,application/pdf"
                                  capture="environment"
                                  id="entrada-camera-input"
                                  hidden
                                  onChange={(e) => handleComprovanteFile(e.target.files?.[0])}
                                  data-testid="input-entrada-camera"
                                />
                                <input
                                  type="file"
                                  accept="image/*,application/pdf"
                                  id="entrada-upload-input"
                                  hidden
                                  onChange={(e) => handleComprovanteFile(e.target.files?.[0])}
                                  data-testid="input-entrada-upload"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="gap-1.5 text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                                  onClick={() => document.getElementById('entrada-camera-input')?.click()}
                                  disabled={uploadingComprovante}
                                  data-testid="btn-entrada-camera"
                                >
                                  <Camera className="h-4 w-4" /> {uploadingComprovante ? "Enviando..." : "Foto"}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="gap-1.5 text-purple-700 border-purple-300 hover:bg-purple-50"
                                  onClick={() => document.getElementById('entrada-upload-input')?.click()}
                                  disabled={uploadingComprovante}
                                  data-testid="btn-entrada-upload"
                                >
                                  <Upload className="h-4 w-4" /> Upload
                                </Button>
                              </div>
                            )}
                            <p className="text-[10px] text-gray-500">Anexe o comprovante do PIX, recibo ou foto da nota.</p>
                          </div>

                          <div className="flex gap-2">
                            <Button type="button" className="flex-1 bg-blue-600 hover:bg-blue-700"
                              data-testid="btn-confirmar-entrada"
                              disabled={uploadingComprovante}
                              onClick={() => {
                                const valor = parseFloat(entradaForm.valor);
                                if (!valor || valor <= 0) { toast.error("Informe o valor"); return; }
                                const totalEntradas = entradas.reduce((a, e) => a + e.valor, 0);
                                if (valor + totalEntradas > totalFinal + 0.01) { toast.error("Pagamentos não podem exceder o total"); return; }
                                if (!entradaForm.imagem_path) { toast.error("Anexe o comprovante (foto ou upload)"); return; }
                                setEntradas(prev => [...prev, { valor, forma_pagamento: entradaForm.forma_pagamento, imagem_path: entradaForm.imagem_path }]);
                                setEntradaForm({ valor: '', forma_pagamento: 'PIX', imagem_path: '' });
                                setShowEntradaForm(false);
                                toast.success(`R$ ${valor.toFixed(2)} adicionado!`);
                              }}>
                              Confirmar
                            </Button>
                            <Button type="button" variant="outline" onClick={() => { setShowEntradaForm(false); setEntradaForm({ valor: '', forma_pagamento: 'PIX', imagem_path: '' }); }}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      )}

                      {entradas.length > 0 && (
                        <div className={`border rounded-lg p-3 ${vendaAvista ? 'bg-emerald-50 border-emerald-300' : 'bg-green-50 border-green-300'}`}>
                          <div className="flex justify-between items-center">
                            <span className={`font-bold ${vendaAvista ? 'text-emerald-800' : 'text-green-800'}`}>
                              {vendaAvista ? 'TOTAL PAGO:' : 'TOTAL ENTRADAS:'}
                            </span>
                            <span className={`text-lg font-bold ${vendaAvista ? 'text-emerald-700' : 'text-green-700'}`}>R$ {entradaValor.toFixed(2)}</span>
                          </div>
                          {totalFinal - entradaValor > 0.01 && (
                            <div className="flex justify-between items-center mt-1">
                              <span className={`font-bold ${vendaAvista ? 'text-emerald-800' : 'text-green-800'}`}>RESTANTE:</span>
                              <span className={`text-xl font-bold ${vendaAvista ? 'text-red-600' : 'text-green-700'}`}>R$ {(totalFinal - entradaValor).toFixed(2)}</span>
                            </div>
                          )}
                          {vendaAvista && avistaQuitado && (
                            <div className="mt-2 flex items-center gap-2 text-emerald-700">
                              <BadgeCheck className="h-4 w-4" />
                              <span className="text-sm font-bold">Valor quitado! Pronto para concluir.</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <Button type="submit" data-testid="save-promissoria-button"
                className={`w-full ${vendaAvista ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-green-500 hover:bg-green-600'}`}
                disabled={submitDisabled}>
                {getSubmitLabel()}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={(o) => {
        // No modo edição, exige imprimir o térmico antes de fechar
        if (!o && modoEdicao && !termicoImpresso) {
          toast.error("Imprima o PDF térmico atualizado antes de fechar.");
          return;
        }
        if (!o) setFotosAnexadas([]);
        setShowPreview(o);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog" aria-describedby="preview-desc"
          onPointerDownOutside={(e) => { if (modoEdicao && !termicoImpresso) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (modoEdicao && !termicoImpresso) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2" style={{fontFamily: 'Work Sans'}}>
              <FileText className="h-6 w-6 text-green-500" />
              {modoEdicao ? 'Nota Atualizada!' : (createdPromissoria?.venda_avista ? 'Venda à Vista Concluída!' : 'Promissoria Criada!')}
            </DialogTitle>
          </DialogHeader>
          <p id="preview-desc" className="sr-only">Previa da promissoria</p>
          {createdPromissoria && (
            <div className="space-y-4 pb-4">
              {createdPromissoria.venda_avista && (
                <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 flex items-center gap-2">
                  <BadgeCheck className="h-5 w-5 text-emerald-600" />
                  <span className="text-emerald-800 font-bold">VENDA À VISTA - QUITADA</span>
                </div>
              )}
              <div className="bg-gradient-to-br from-green-50 to-blue-50 p-5 rounded-lg border-2 border-green-200 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-gray-500 font-semibold">CLIENTE</p><p className="text-base font-bold">{createdPromissoria.cliente_nome}</p></div>
                  <div><p className="text-xs text-gray-500 font-semibold">N. PEDIDO</p><p className="text-base font-bold text-blue-600">{createdPromissoria.numero_pedido}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-gray-500 font-semibold">VALOR TOTAL</p><p className="text-xl font-bold text-green-600">R$ {createdPromissoria.valor_total.toFixed(2)}</p></div>
                  <div><p className="text-xs text-gray-500 font-semibold">{createdPromissoria.venda_avista ? 'STATUS' : 'VENCIMENTO'}</p>
                    <p className="text-base font-bold">{createdPromissoria.venda_avista ? 'QUITADA' : formatDate(createdPromissoria.data_vencimento)}</p>
                  </div>
                </div>

                {/* Entradas / Pagamentos */}
                {createdPromissoria.pagamentos && createdPromissoria.pagamentos.length > 0 && (
                  <div className="space-y-2">
                    {createdPromissoria.pagamentos.map((pag, idx) => (
                      <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-600 font-semibold">
                          {createdPromissoria.venda_avista ? `PAGAMENTO ${idx+1}` : `ENTRADA ${idx+1}`} ({pag.forma_pagamento})
                        </p>
                        <p className="text-lg font-bold text-blue-700">R$ {pag.valor.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Itens da compra */}
                {createdPromissoria.itens && createdPromissoria.itens.length > 0 && (() => {
                  const vendas = createdPromissoria.itens.filter(i => (i.tipo || 'venda') !== 'retorno');
                  const retornos = createdPromissoria.itens.filter(i => i.tipo === 'retorno');
                  return (
                    <div className="space-y-2">
                      {vendas.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 font-semibold mb-1">PRODUTOS</p>
                          <table className="w-full text-sm border">
                            <thead className="bg-gray-100"><tr><th className="text-left p-1.5 border-b">Produto</th><th className="text-center p-1.5 border-b">Qtd</th><th className="text-right p-1.5 border-b">Unit.</th><th className="text-right p-1.5 border-b">Subtotal</th></tr></thead>
                            <tbody>{vendas.map((item, idx) => (
                              <tr key={idx} className="border-b"><td className="p-1.5">{item.tipo === 'kit' && <span className="inline-block bg-pink-100 text-pink-800 font-bold rounded px-1 mr-1 text-[10px]">KIT</span>}{item.produto_nome}</td><td className="text-center p-1.5">{item.quantidade}</td><td className="text-right p-1.5">R$ {item.preco_unitario?.toFixed(2)}</td><td className="text-right p-1.5 font-semibold">R$ {item.subtotal?.toFixed(2)}</td></tr>
                            ))}</tbody>
                          </table>
                        </div>
                      )}
                      {retornos.length > 0 && (
                        <div>
                          <p className="text-xs text-orange-600 font-semibold mb-1">RETORNO</p>
                          <table className="w-full text-sm border border-orange-200">
                            <thead className="bg-orange-50"><tr><th className="text-left p-1.5 border-b">Produto</th><th className="text-center p-1.5 border-b">Qtd</th><th className="text-right p-1.5 border-b">Unit.</th><th className="text-right p-1.5 border-b">Desconto</th></tr></thead>
                            <tbody>{retornos.map((item, idx) => (
                              <tr key={idx} className="border-b border-orange-100"><td className="p-1.5 text-orange-700">{item.produto_nome}</td><td className="text-center p-1.5">{item.quantidade}</td><td className="text-right p-1.5">R$ {item.preco_unitario?.toFixed(2)}</td><td className="text-right p-1.5 font-semibold text-orange-600">- R$ {item.subtotal?.toFixed(2)}</td></tr>
                            ))}</tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="flex gap-3">
                <Button onClick={handleDownloadPDF} className={`flex-1 gap-2 ${modoEdicao && !termicoImpresso ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-blue-500 hover:bg-blue-600'}`} data-testid="download-preview-button">
                  <Download className="h-4 w-4" /> {modoEdicao ? (termicoImpresso ? 'PDF Térmico (Reimprimir)' : 'Imprimir PDF Térmico *') : 'Baixar Recibo'}
                </Button>
                <Button onClick={handleShareWhatsApp} className="flex-1 gap-2 bg-green-600 hover:bg-green-700" data-testid="share-whatsapp-button">
                  <Share2 className="h-4 w-4" /> Enviar WhatsApp
                </Button>
              </div>
              {!modoEdicao && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    id="foto-nota-preview-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      handleUploadFotoNota(f);
                      e.target.value = "";
                    }}
                    data-testid="foto-nota-preview-input"
                  />
                  <Button
                    onClick={() => document.getElementById('foto-nota-preview-input')?.click()}
                    disabled={uploadingFotoNota}
                    className={`w-full gap-2 ${fotosAnexadas.length === 0 ? 'bg-red-600 hover:bg-red-700 animate-pulse text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                    data-testid="btn-foto-nota-preview"
                  >
                    <Camera className="h-4 w-4" />
                    {uploadingFotoNota
                      ? 'Enviando...'
                      : fotosAnexadas.length === 0
                        ? 'Tirar Foto da Nota'
                        : `Foto anexada (${fotosAnexadas.length}) — Tirar outra`}
                  </Button>
                </>
              )}
              {modoEdicao && !termicoImpresso && (
                <div className="w-full rounded-lg border-2 border-red-500 bg-red-50 py-3 px-4 text-center animate-pulse" data-testid="aviso-termico-edicao">
                  <p className="text-red-700 font-extrabold uppercase tracking-wide text-sm">
                    Imprima o PDF térmico atualizado antes de fechar!
                  </p>
                </div>
              )}
              <Button
                onClick={() => { setShowPreview(false); navigate('/dividas'); }}
                disabled={modoEdicao && !termicoImpresso}
                variant="outline"
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="btn-fechar-preview"
                title={modoEdicao && !termicoImpresso ? 'Imprima o PDF térmico para liberar' : ''}
              >
                {modoEdicao ? 'Concluir Edição' : 'Ver Todas as Dividas'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Comprovante (entrada) */}
      <Dialog open={!!previewComprovantePath} onOpenChange={(o) => { if (!o) setPreviewComprovantePath(null); }}>
        <DialogContent className="max-w-lg" aria-describedby="preview-comp-desc">
          <DialogHeader>
            <DialogTitle>Comprovante</DialogTitle>
          </DialogHeader>
          <p id="preview-comp-desc" className="sr-only">Visualização do comprovante</p>
          {previewComprovantePath && (
            previewComprovantePath.toLowerCase().endsWith('.pdf') ? (
              <iframe src={`${API}/caixa/imagem/${previewComprovantePath}`} title="Comprovante" className="w-full h-80 border-0" />
            ) : (
              <img src={`${API}/caixa/imagem/${previewComprovantePath}`} alt="Comprovante" className="w-full max-h-80 object-contain bg-white" />
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
